import { __assign } from "tslib";
import { composer } from '../../../component-decorators/composer';
import s from './solution-page.scss';
import React, { useState, useEffect } from 'react';
import { ExternalLink, Confirm, ConfirmSmall } from 'wix-ui-icons-common';
import { Text, Heading, Card, Divider, TextButton, Box, SegmentedToggle, } from 'wix-style-react';
import { biLogger } from '../../../bi';
import { PlanDescription, IntervalUnit, TaxSettingsTaxType, } from '@wix/ambassador-marketplace/types';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { isWixApp } from '../../../common/utils';
import { AppPageMode } from '../../../enums/marketplace-enums';
import { useConfigContext } from "../../../config-context";
export var dtoToSolutionPlansProps = function (solutionDto, mode) {
    var _a, _b, _c;
    return {
        appId: solutionDto.baseInfo.id,
        companyName: solutionDto.baseInfo.company.name,
        companyId: solutionDto.baseInfo.company.id,
        plans: (_a = solutionDto.baseInfo.pricing) === null || _a === void 0 ? void 0 : _a.plans,
        isExternalPricing: solutionDto.baseInfo.pricing &&
            solutionDto.baseInfo.pricing.isExternalPricing,
        externalPricingLink: solutionDto.baseInfo.pricing &&
            solutionDto.baseInfo.pricing.externalPricingPageUrl,
        trialDays: solutionDto &&
            solutionDto.baseInfo &&
            solutionDto.baseInfo.pricing &&
            solutionDto.baseInfo.pricing.freeTrialDays,
        businessModel: solutionDto.baseInfo.pricing &&
            solutionDto.baseInfo.pricing.businessModel,
        currencySettings: (_b = solutionDto.baseInfo.pricing) === null || _b === void 0 ? void 0 : _b.currencySettings,
        taxSettings: (_c = solutionDto.baseInfo.pricing) === null || _c === void 0 ? void 0 : _c.taxSettings,
        mode: mode,
    };
};
var shouldShowPricingToggle = function (plans, showPremiumPrices) {
    if (!showPremiumPrices) {
        return plans === null || plans === void 0 ? void 0 : plans.some(function (plan) { return plan.yearlyDiscountPercent > 0; });
    }
    return plans === null || plans === void 0 ? void 0 : plans.some(function (plan) {
        var _a, _b, _c, _d;
        return ((_b = (_a = plan.premiumPrices) === null || _a === void 0 ? void 0 : _a.fullPrices) === null || _b === void 0 ? void 0 : _b.some(function (prices) { return prices.paymentCycle.interval.unit === IntervalUnit.MONTH; })) && ((_d = (_c = plan.premiumPrices) === null || _c === void 0 ? void 0 : _c.fullPrices) === null || _d === void 0 ? void 0 : _d.some(function (prices) { return prices.paymentCycle.interval.unit === IntervalUnit.YEAR; }));
    });
};
var getInitialPricingToggleState = function (plans, showFullPrices, externalPricing) {
    return shouldShowPricingToggle(plans, showFullPrices && !externalPricing)
        ? "YEARLY" /* YEARLY */
        : "NONE" /* NONE */;
};
export var SolutionPlans = composer()
    .withTranslation()
    .withExperiments()
    .compose(function (props) {
    // tslint:disable-next-line: react-hooks-nesting
    var _a = useState(props.appId), appId = _a[0], setAppId = _a[1];
    var trialDays = props.trialDays && Number(props.trialDays);
    var businessModelDynamic = props.businessModel === 'DYNAMIC';
    var hasPlanWithPrice = props.plans &&
        props.plans.filter(function (plan) { return plan.isCustom === false && plan.name !== 'Free'; }).length >= 1;
    var showFullPrices = props.experiments.enabled('specs.marketplace.fullPrices');
    function getPricingCurrencyText() {
        var _a;
        var _b, _c;
        var tax = (_b = props.taxSettings) === null || _b === void 0 ? void 0 : _b.displayTaxType;
        var currency = (_c = props.currencySettings) === null || _c === void 0 ? void 0 : _c.code;
        var companyName = props.companyName;
        var TAXES = (_a = {},
            _a[TaxSettingsTaxType.GST] = "solution.page.pricing.gst",
            _a[TaxSettingsTaxType.VAT] = "solution.page.pricing.vat",
            _a[TaxSettingsTaxType.NOT_APPLICABLE] = "solution.page.pricing.tax",
            _a);
        var taxName = props.t(TAXES[tax]);
        if (props.mode === AppPageMode.PREVIEW || props.mode === AppPageMode.TEST) {
            return props.t('solution.page.pricingCurrency');
        }
        if (!showFullPrices && props.isExternalPricing) {
            return props.t('solution.page.external.pricingCurrency', { currency: currency, companyName: companyName });
        }
        if (!showFullPrices && !props.isExternalPricing) {
            return props.t('solution.page.pricingCurrency', { currency: currency });
        }
        if (props.isExternalPricing && props.taxSettings.showPricesWithTax) {
            return props.t('solution.page.external.pricingCurrencyTaxAll', { currency: currency, taxName: taxName, companyName: companyName });
        }
        if (props.isExternalPricing && !props.taxSettings.showPricesWithTax) {
            return props.t('solution.page.external.pricingCurrencyAll', { currency: currency, companyName: companyName });
        }
        if (!props.isExternalPricing && props.taxSettings.showPricesWithTax) {
            return props.t('solution.page.pricingCurrencyTaxAll', { currency: currency, taxName: taxName });
        }
        return props.t('solution.page.pricingCurrencyAll', { currency: currency });
    }
    var companyId = props.companyId;
    // tslint:disable-next-line: react-hooks-nesting
    var _b = useState(function () {
        return getInitialPricingToggleState(props.plans, showFullPrices, props.isExternalPricing);
    }), togglePricing = _b[0], setTogglePricing = _b[1];
    var showPriceDisclaimer = !businessModelDynamic && hasPlanWithPrice;
    var changeTogglePricing = function (newValue) {
        biLogger.appmarketPackagePickerToggleCycleClick({
            cycle_click: newValue,
        });
        setTogglePricing(newValue);
    };
    // tslint:disable-next-line: react-hooks-nesting
    useEffect(function () {
        if (appId !== props.appId) {
            setAppId(props.appId);
            setTogglePricing(getInitialPricingToggleState(props.plans, showFullPrices, props.isExternalPricing));
        }
        if (togglePricing === "NONE" /* NONE */ &&
            shouldShowPricingToggle(props.plans, showFullPrices && !props.isExternalPricing)) {
            setTogglePricing("YEARLY" /* YEARLY */);
        }
    });
    function getExternalPricing(className) {
        return (React.createElement(React.Fragment, null, props.isExternalPricing && props.externalPricingLink ? (React.createElement(TextButton, { dataHook: "external-pricing-link", className: className, suffixIcon: React.createElement(ExternalLink, { size: "24" }), as: "a", href: props.externalPricingLink, target: "_blank" }, props.t('solution.page.comparePlans'))) : null));
    }
    return props.plans && props.plans.length && !isWixApp(companyId) ? (React.createElement("div", { "data-testid": "pricing-plans-section", property: "offers", typeof: "Offer" },
        React.createElement("div", { className: s.reviewsPricingDivider },
            React.createElement(Divider, null)),
        React.createElement(Box, { direction: "vertical" },
            React.createElement(Box, { direction: "horizontal", align: "space-between" },
                React.createElement("div", { className: s.pricingPlansTitle },
                    React.createElement("span", null,
                        React.createElement(Heading, { appearance: "H2", className: s.pricingTitle }, props.t('solution.page.pricingAndPlans')),
                        React.createElement(Text, null, trialDays !== 0 &&
                            props.t('solution.page.pricing.trialDays', { trialDays: trialDays }))),
                    getExternalPricing(s.externalPricingLink))),
            shouldShowPricingToggle(props.plans, showFullPrices && !props.isExternalPricing) ? (React.createElement(TogglePricing, { selectedValue: togglePricing, onClick: changeTogglePricing })) : null,
            React.createElement(PlansLayoutMaxWidth, { plans: props.plans, togglePricing: togglePricing, currencySettings: props.currencySettings, taxSettings: props.taxSettings, mode: props.mode }),
            getExternalPricing(s.externalMobile),
            showPriceDisclaimer && (React.createElement(Text, { size: "tiny", secondary: true, weight: "thin", skin: "standard", className: s.pricingPlanCurrency }, getPricingCurrencyText()))),
        React.createElement("meta", { property: "priceCurrency", content: "USD" }))) : null;
});
var TogglePricing = composer()
    .withTranslation()
    .compose(function (props) {
    return (React.createElement("div", { className: s.pricingToggle },
        React.createElement(SegmentedToggle, { selected: props.selectedValue, onClick: function (e, value) { return props.onClick(value); }, dataHook: "toggle-pricing" },
            React.createElement(SegmentedToggle.Button, { value: "YEARLY" /* YEARLY */, dataHook: "toggle-pricing-yearly" },
                React.createElement(Text, null, props.t('solution.page.togglePricing.Yearly'))),
            React.createElement(SegmentedToggle.Button, { value: "MONTHLY" /* MONTHLY */, dataHook: "toggle-pricing-monthly" },
                React.createElement(Text, null, props.t('solution.page.togglePricing.Monthly'))))));
});
var PlansLayoutMaxWidth = composer().compose(function (props) {
    return (React.createElement("div", { className: s.plansMinLayoutDynamic }, props.plans.map(function (plan, index, array) { return (React.createElement(BoxPlanWithDynamics, { span: array.length > 3 ? 3 : 4, plan: plan, key: plan.name, togglePricing: props.togglePricing, currencySettings: props.currencySettings, taxSettings: props.taxSettings, mode: props.mode })); })));
});
var CustomTextBoxPlan = composer()
    .withTranslation()
    .compose(function (props) {
    return (React.createElement(Text, { weight: "thin", className: s.planCustomText }, props.t(props.plan.customText)));
});
var BenefitsBoxPlan = composer()
    .withTranslation()
    .compose(function (props) {
    // tslint:disable-next-line:react-hooks-nesting
    var isMobile = useConfigContext().isMobile;
    return (React.createElement(React.Fragment, null, props.plan.benefits &&
        props.plan.benefits.length > 0 &&
        props.plan.benefits.map(function (benefit, index, array) { return (React.createElement(PricingFeature, { key: index, text: benefit, index: index, last: array.length - 1 === index, isMobile: isMobile })); })));
});
var PricingBoxPlan = composer()
    .withTranslation()
    .withExperiments()
    .compose(function (props) {
    var showFullPrices = props.experiments.enabled('specs.marketplace.fullPrices');
    return (React.createElement(Box, null,
        React.createElement(Heading, { appearance: "H2", className: s.planPricingBoxBigDynamic }, planPricing(props.plan, props.t, props.togglePricing, showFullPrices, props.currencySettings, props.taxSettings, props.mode)),
        React.createElement(Box, { paddingBottom: '3px', className: s.planPricingPeriodSubTitle, align: 'center' },
            React.createElement(Text, { weight: "bold" }, planPricingPeriod(props.plan, props.t)))));
});
var BoxPlanWithDynamics = composer()
    .withTranslation()
    .withExperiments()
    .compose(function (props) {
    var _a;
    var pricingPlanName = props.plan.name || '';
    var isDynamicPlan = props.plan.isCustom;
    var isCustomText = ((_a = props.plan) === null || _a === void 0 ? void 0 : _a.planDescription) === PlanDescription.CUSTOM_TEXT;
    return (React.createElement(Card, { className: s.pricingPlanContainerDynamic },
        React.createElement(Card.Content, null,
            React.createElement(Box, { direction: 'horizontal', align: 'space-between', marginTop: '6px' },
                React.createElement(Heading, { appearance: "H5", dataHook: "pricing-plan-" + pricingPlanName.toLowerCase() }, props.t('solution.page.planType', {
                    planType: props.plan.name.toUpperCase(),
                }))),
            React.createElement(Box, { className: s.planTitleBoxDynamic, direction: "horizontal", verticalAlign: "bottom" }, isDynamicPlan ? (React.createElement(Heading, { appearance: "H2", className: s.planTitleBoxBig }, props.t(props.plan.title))) : (React.createElement(PricingBoxPlan, __assign({}, props)))),
            React.createElement(Box, { direction: "vertical", align: "left" }, isDynamicPlan && isCustomText ? (React.createElement(CustomTextBoxPlan, __assign({}, props))) : (React.createElement(BenefitsBoxPlan, __assign({}, props)))))));
});
var planPricing = function (_a, t, togglePricing, showFullPrices, currencySettings, taxSettings, mode) {
    var _b;
    var yearlyPrice = _a.yearlyPrice, monthlyPrice = _a.monthlyPrice, premiumPrices = _a.premiumPrices;
    if (togglePricing === void 0) { togglePricing = "NONE" /* NONE */; }
    var hasPrice = yearlyPrice || monthlyPrice;
    if (!hasPrice) {
        return (React.createElement(React.Fragment, null,
            React.createElement("meta", { property: "price", content: "0" }),
            t('solution.page.freePlanTitle')));
    }
    // tslint:disable-next-line:react-hooks-nesting
    var currencyToString = usePremiumCurrencyFormatter({
        currencySettings: (_b = {},
            _b[currencySettings === null || currencySettings === void 0 ? void 0 : currencySettings.code] = {
                symbol: currencySettings === null || currencySettings === void 0 ? void 0 : currencySettings.symbol,
                decimalSep: currencySettings === null || currencySettings === void 0 ? void 0 : currencySettings.decimalSeparator,
                groupSep: currencySettings === null || currencySettings === void 0 ? void 0 : currencySettings.groupSeparator,
                negPrefix: currencySettings === null || currencySettings === void 0 ? void 0 : currencySettings.negativePrefix,
                negSuffix: currencySettings === null || currencySettings === void 0 ? void 0 : currencySettings.negativeSuffix,
                posPrefix: '¤',
                posSuffix: '',
                fractionSize: 2,
            },
            _b),
        currencyCode: currencySettings === null || currencySettings === void 0 ? void 0 : currencySettings.code,
    })[0];
    var showPremium = showFullPrices &&
        premiumPrices &&
        mode !== AppPageMode.TEST &&
        mode !== AppPageMode.PREVIEW;
    var getPremiumPrice = function (intervalUnit) {
        var premiumPrice = premiumPrices === null || premiumPrices === void 0 ? void 0 : premiumPrices.fullPrices.find(function (prices) { return prices.paymentCycle.interval.unit === intervalUnit; });
        var relevantPremiumPrice = (taxSettings === null || taxSettings === void 0 ? void 0 : taxSettings.showPricesWithTax) ? premiumPrice === null || premiumPrice === void 0 ? void 0 : premiumPrice.monthlyPriceWithTax : premiumPrice === null || premiumPrice === void 0 ? void 0 : premiumPrice.monthlyPriceBeforeTax;
        return currencyToString(Number(relevantPremiumPrice));
    };
    var getDCPrice = function (unitPrice) {
        var dcPrice;
        if (showFullPrices && mode !== AppPageMode.TEST && mode !== AppPageMode.PREVIEW) {
            dcPrice = currencyToString(Number(unitPrice));
        }
        else {
            dcPrice = "$" + unitPrice;
        }
        return dcPrice;
    };
    var getMonthOrYear = function () {
        function getInterval() {
            return (premiumPrices === null || premiumPrices === void 0 ? void 0 : premiumPrices.fullPrices.some(function (plan) { return plan.paymentCycle.interval.unit === IntervalUnit.MONTH; })) ? IntervalUnit.MONTH : IntervalUnit.YEAR;
        }
        switch (togglePricing) {
            case "YEARLY" /* YEARLY */:
                return showPremium ? IntervalUnit.YEAR : yearlyPrice;
            case "MONTHLY" /* MONTHLY */:
                return showPremium ? IntervalUnit.MONTH : monthlyPrice;
            case "NONE" /* NONE */:
                return showPremium ? getInterval() : monthlyPrice;
            default:
                return '';
        }
    };
    var price = showPremium ? getPremiumPrice(getMonthOrYear()) : getDCPrice(getMonthOrYear());
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { "data-hook": "solution-plan-price", property: "price" }, price)));
};
var planPricingPeriod = function (_a, t) {
    var yearlyPrice = _a.yearlyPrice, monthlyPrice = _a.monthlyPrice;
    return yearlyPrice || monthlyPrice
        ? "  / " + t('solution.page.pricingMonth')
        : '';
};
var PricingFeature = function (_a) {
    var text = _a.text, index = _a.index, last = _a.last, isMobile = _a.isMobile;
    return (React.createElement(Box, { key: index, align: "center", direction: "horizontal", verticalAlign: "middle", marginBottom: last || isMobile ? '' : '2', inline: true },
        React.createElement("div", { className: s.stylingBenefit },
            React.createElement("div", { className: s.stylingConfirm }, isMobile ? React.createElement(ConfirmSmall, null) : React.createElement(Confirm, null)),
            React.createElement(Text, { weight: "thin", dataHook: "pricefeature-" + text, className: s.benefitTextBigBoxDynamic }, text))));
};
//# sourceMappingURL=solution-plans.js.map